/**
 * Registration service — wraps the onboarding API layer with
 * mapper-integrated helper functions.
 */
import onboardingApi from '../onboarding/onboardingApi';
import {
  mapBasicInfoToProfileInfoRequest,
  mapSurveyToQuestionnaireRequest,
  mapSurveyToBuddyPreferences,
  BUDDY_PREFERENCES_UPDATED_EVENT,
  type BasicInfoFormState,
} from '../onboarding/onboardingMapper';
import { STORAGE_KEYS } from '../../constants/storage';
import { storage } from '../../platform/storage';
import { eventBus } from '../../platform/eventBus';
import type { SurveyState } from '../../types/survey';
import type { CreateAccountRequest, BuddyMatch } from '../onboarding/onboardingTypes';

const registrationService = {
  /** Step 1: Create account — delegates directly to onboardingApi. */
  createAccount: (payload: CreateAccountRequest) =>
    onboardingApi.createAccount(payload),

  /**
   * Step 2: Save basic profile info as multipart/form-data
   * (includes ProfilePicture binary when selected).
   */
  saveProfileInfo: (state: BasicInfoFormState) =>
    onboardingApi.saveProfileInfo(mapBasicInfoToProfileInfoRequest(state)),

  /**
   * Step 3: Submit questionnaire and receive a buddy match.
   * Maps SurveyContext state + userGUID to the API request.
   * Immediately updates the Buddy Preferences Store/storage with the returned/saved preferences.
   */
  submitQuestionnaire: async (
    survey: SurveyState,
    userGUID: string,
  ): Promise<BuddyMatch> => {
    const payload = mapSurveyToQuestionnaireRequest(survey, userGUID);
    const { data } = await onboardingApi.submitQuestionnaire(payload);

    // Immediately update Buddy Preferences Store with the latest survey preferences
    const buddyPreferences = mapSurveyToBuddyPreferences(survey, userGUID);
    await storage.setItem(STORAGE_KEYS.BUDDY_PREFERENCES, JSON.stringify(buddyPreferences));
    eventBus.emit(BUDDY_PREFERENCES_UPDATED_EVENT, buddyPreferences);

    return data;
  },
};

export default registrationService;
